import { Injectable, inject } from '@angular/core';
import {
  EllaConnectionClient,
  EllaPipelineClient,
  EllaIntegrationClient,
  EllaMdhClient,
  EllaHealthClient,
  EllaIssueClient,
  EllaAlertClient,
  EllaSettingsClient,
  EllaKeyVaultClient,
  EllaDatabricksMetadataClient,
  EllaDatabricksConnectionClient,
  EllaSqlConnectionClient,
  EllaPromotionClient,
  EllaEnvironmentVariableClient,
  EllaAdapterClient,
  EllaAdapterBuilderClient,
  CreateEllaEnvironmentVariableRequest,
  UpdateEllaEnvironmentVariableRequest,
  ExportPromotionPackageRequest,
  CreateEllaConnectionDto,
  UpdateEllaConnectionDto,
  AssociateEllaConnectionIntegrationDto,
  BulkSetConnectionsLockedDto,
  CreateEllaDatabricksConnectionRequest,
  UpdateEllaDatabricksConnectionRequest,
  AssociateEllaDatabricksConnectionIntegrationRequest,
  CreateEllaSqlConnectionRequest,
  UpdateEllaSqlConnectionRequest,
  AssociateEllaSqlConnectionIntegrationRequest,
  CreateEllaConnectionPolicyDto,
  UpdateEllaConnectionPolicyDto,
  EllaProxyRequestDto,
  CreateEllaKeyVaultDto,
  UpdateEllaKeyVaultDto,
  CreateEllaKeyVaultSecretDto,
  UpdateEllaKeyVaultSecretDto,
  CreateEllaPipelineDto,
  UpdateEllaPipelineDto,
  BulkCreatePipelinesDto,
  BulkSetPipelinesActiveDto,
  BulkSetPipelinesSwaggerVisibleDto,
  BulkSetPipelinesLockedDto,
  BulkDeletePipelinesDto,
  ExecuteEllaPipelineDto,
  EllaPipelineDebugRequestDto,
  CreateEllaMdhUniverseDto,
  UpdateEllaMdhUniverseDto,
  IngestEllaMdhSourceRecordDto,
  ResolveQuarantineDto,
  CreateEllaHealthCheckDto,
  UpdateEllaHealthCheckDto,
  CreateEllaAlertRuleDto,
  UpdateEllaAlertRuleDto,
  UpdateEllaSystemSettingDto,
  CreateEllaIntegrationDto,
  UpdateEllaIntegrationDto,
  UpdateEllaIntegrationMachineCallerPolicyRequest,
  UpdateEllaIntegrationApiKeyCallerPolicyRequest,
  PreviewSpecImportDto,
  ApplySpecImportDto,
  CheckDatabricksColumnChangesRequest,
  TestDatabricksGenieRequest,
  PublishPipelineRequest,
  CreateEllaAdapterRequest,
  UpdateEllaAdapterDraftRequest,
  ImportAdapterOperationsFromSpecRequest,
  TestAdapterOperationRequest,
  PublishEllaAdapterRequest,
  RenameEllaAdapterRequest,
  GetAdapterOperationUsageRequest,
  FileParameter,
  EllaProxySpClient,
  EllaApiKeyClient,
  EllaApiKeyProxySpMappingClient,
  EllaApiKeyScopeClient,
  EllaCallerSpMappingClient,
  CreateEllaApiKeyRequest,
  RotateEllaApiKeyRequest,
  CreateEllaApiKeyProxySpMappingRequest,
  GrantIntegrationScopeRequest,
  GrantPipelineScopeRequest,
  CreateEllaProxySpRequest,
  UpdateEllaProxySpRequest,
  CreateEllaCallerSpMappingRequest,
  UpdateEllaCallerSpMappingRequest
} from '../../../core/api/galaxy-api-client';

@Injectable({ providedIn: 'root' })
export class EllaApiService {
  private connection = inject(EllaConnectionClient);
  private process = inject(EllaPipelineClient);
  private integration = inject(EllaIntegrationClient);
  private mdh = inject(EllaMdhClient);
  private health = inject(EllaHealthClient);
  private issue = inject(EllaIssueClient);
  private alert = inject(EllaAlertClient);
  private settings = inject(EllaSettingsClient);
  private keyVault = inject(EllaKeyVaultClient);
  private databricksMeta = inject(EllaDatabricksMetadataClient);
  private databricksConnection = inject(EllaDatabricksConnectionClient);
  private sqlConnection = inject(EllaSqlConnectionClient);
  private promotion = inject(EllaPromotionClient);
  private environmentVariable = inject(EllaEnvironmentVariableClient);
  private adapter = inject(EllaAdapterClient);
  private adapterBuilder = inject(EllaAdapterBuilderClient);
  private proxySp = inject(EllaProxySpClient);
  private apiKey = inject(EllaApiKeyClient);
  private apiKeyProxySpMapping = inject(EllaApiKeyProxySpMappingClient);
  private apiKeyScope = inject(EllaApiKeyScopeClient);
  private callerSpMapping = inject(EllaCallerSpMappingClient);

  // --- Connections ---
  getConnections(integrationId?: number) { return this.connection.getConnections(integrationId); }
  getConnection(id: number) { return this.connection.getConnection(id); }
  createConnection(dto: CreateEllaConnectionDto) { return this.connection.createConnection(dto); }
  updateConnection(id: number, dto: UpdateEllaConnectionDto) { return this.connection.updateConnection(id, dto); }
  deleteConnection(id: number) { return this.connection.deleteConnection(id); }
  getConnectionUsage(id: number) { return this.connection.getUsage(id); }
  associateConnectionIntegration(id: number, integrationId: number) { return this.connection.associateIntegration(id, new AssociateEllaConnectionIntegrationDto({ integrationId })); }
  disassociateConnectionIntegration(id: number, integrationId: number) { return this.connection.disassociateIntegration(id, integrationId); }
  getPolicies(connectionId: number) { return this.connection.getPolicies(connectionId); }
  createPolicy(dto: CreateEllaConnectionPolicyDto) { return this.connection.createPolicy(dto); }
  updatePolicy(policyId: number, dto: UpdateEllaConnectionPolicyDto) { return this.connection.updatePolicy(policyId, dto); }
  deletePolicy(policyId: number) { return this.connection.deletePolicy(policyId); }
  proxy(connectionId: number, req: EllaProxyRequestDto) { return this.connection.proxy(connectionId, req); }
  getRequestLogs(connectionId: number, limit?: number) { return this.connection.getRequestLogs(connectionId, limit); }
  testConnection(id: number) { return this.connection.testConnection(id); }
  lockConnection(id: number) { return this.connection.lock(id); }
  unlockConnection(id: number) { return this.connection.unlock(id); }
  bulkSetConnectionsLocked(dto: BulkSetConnectionsLockedDto) { return this.connection.bulkSetConnectionsLocked(dto); }
  startDeviceCodeSignIn(id: number) { return this.connection.startDeviceCodeSignIn(id); }
  getDeviceCodeSignInStatus(id: number) { return this.connection.getDeviceCodeSignInStatus(id); }

  // --- Integrations ---
  getIntegrations() { return this.integration.getIntegrations(); }
  getIntegration(id: number) { return this.integration.getIntegration(id); }
  createIntegration(dto: CreateEllaIntegrationDto) { return this.integration.createIntegration(dto); }
  updateIntegration(id: number, dto: UpdateEllaIntegrationDto) { return this.integration.updateIntegration(id, dto); }
  deleteIntegration(id: number) { return this.integration.deleteIntegration(id); }
  updateMachineCallerPolicy(id: number, request: UpdateEllaIntegrationMachineCallerPolicyRequest) { return this.integration.updateMachineCallerPolicy(id, request); }
  updateApiKeyCallerPolicy(id: number, request: UpdateEllaIntegrationApiKeyCallerPolicyRequest) { return this.integration.updateApiKeyCallerPolicy(id, request); }
  getRelationshipMap(integrationId: number) { return this.integration.getRelationshipMap(integrationId); }
  lockIntegration(id: number) { return this.integration.lock(id); }
  unlockIntegration(id: number) { return this.integration.unlock(id); }

  // --- Databricks metadata (pipeline wizard) ---
  getDatabricksCatalogs(connectionId: number) { return this.databricksMeta.getCatalogs(connectionId); }
  getDatabricksSchemas(connectionId: number, catalog: string) { return this.databricksMeta.getSchemas(catalog, connectionId); }
  getDatabricksObjects(connectionId: number, catalog: string, schema: string) { return this.databricksMeta.getObjects(schema, catalog, connectionId); }
  getDatabricksColumns(connectionId: number, catalog: string, schema: string, object: string) { return this.databricksMeta.getColumns(object, schema, catalog, connectionId); }
  checkDatabricksColumnChanges(request: CheckDatabricksColumnChangesRequest) { return this.databricksMeta.checkColumnChanges(request); }

  // --- Databricks connections ---
  getDatabricksConnections(integrationId?: number) { return this.databricksConnection.getConnections(integrationId); }
  getDatabricksConnection(id: number) { return this.databricksConnection.getConnection(id); }
  createDatabricksConnection(dto: CreateEllaDatabricksConnectionRequest) { return this.databricksConnection.createConnection(dto); }
  updateDatabricksConnection(id: number, dto: UpdateEllaDatabricksConnectionRequest) { return this.databricksConnection.updateConnection(id, dto); }
  deleteDatabricksConnection(id: number) { return this.databricksConnection.deleteConnection(id); }
  getDatabricksConnectionUsage(id: number) { return this.databricksConnection.getUsage(id); }
  testDatabricksConnection(id: number) { return this.databricksConnection.testConnection(id); }
  associateDatabricksConnectionIntegration(id: number, integrationId: number) { return this.databricksConnection.associateIntegration(id, new AssociateEllaDatabricksConnectionIntegrationRequest({ integrationId })); }
  disassociateDatabricksConnectionIntegration(id: number, integrationId: number) { return this.databricksConnection.disassociateIntegration(id, integrationId); }
  lockDatabricksConnection(id: number) { return this.databricksConnection.lock(id); }
  unlockDatabricksConnection(id: number) { return this.databricksConnection.unlock(id); }
  bulkSetDatabricksConnectionsLocked(dto: BulkSetConnectionsLockedDto) { return this.databricksConnection.bulkSetConnectionsLocked(dto); }

  // --- SQL connections ---
  getSqlConnections(integrationId?: number) { return this.sqlConnection.getConnections(integrationId); }
  getSqlConnection(id: number) { return this.sqlConnection.getConnection(id); }
  createSqlConnection(dto: CreateEllaSqlConnectionRequest) { return this.sqlConnection.createConnection(dto); }
  updateSqlConnection(id: number, dto: UpdateEllaSqlConnectionRequest) { return this.sqlConnection.updateConnection(id, dto); }
  deleteSqlConnection(id: number) { return this.sqlConnection.deleteConnection(id); }
  getSqlConnectionUsage(id: number) { return this.sqlConnection.getUsage(id); }
  testSqlConnection(id: number) { return this.sqlConnection.testConnection(id); }
  associateSqlConnectionIntegration(id: number, integrationId: number) { return this.sqlConnection.associateIntegration(id, new AssociateEllaSqlConnectionIntegrationRequest({ integrationId })); }
  disassociateSqlConnectionIntegration(id: number, integrationId: number) { return this.sqlConnection.disassociateIntegration(id, integrationId); }
  lockSqlConnection(id: number) { return this.sqlConnection.lock(id); }
  unlockSqlConnection(id: number) { return this.sqlConnection.unlock(id); }
  bulkSetSqlConnectionsLocked(dto: BulkSetConnectionsLockedDto) { return this.sqlConnection.bulkSetConnectionsLocked(dto); }

  // --- Pipeline Builder ---
  getPipelines(
    integrationId?: number, page?: number, pageSize?: number, searchText?: string,
    triggerType?: string, isActive?: boolean, sortBy?: string, sortDesc?: boolean
  ) {
    return this.process.getPipelines(page, pageSize, sortBy, sortDesc, integrationId, searchText, triggerType, isActive);
  }
  getPipeline(id: number) { return this.process.getPipeline(id); }
  getCachingProfile(id: number) { return this.process.getCachingProfile(id); }
  getPipelinesByIds(ids: number[]) { return this.process.getPipelinesByIds(ids); }
  createPipeline(dto: CreateEllaPipelineDto) { return this.process.createPipeline(dto); }
  updatePipeline(id: number, dto: UpdateEllaPipelineDto) { return this.process.updatePipeline(id, dto); }
  deletePipeline(id: number) { return this.process.deletePipeline(id); }
  bulkCreatePipelines(dto: BulkCreatePipelinesDto) { return this.process.bulkCreatePipelines(dto); }
  bulkSetPipelinesActive(dto: BulkSetPipelinesActiveDto) { return this.process.bulkSetPipelinesActive(dto); }
  bulkSetPipelinesSwaggerVisible(dto: BulkSetPipelinesSwaggerVisibleDto) { return this.process.bulkSetPipelinesSwaggerVisible(dto); }
  bulkSetPipelinesLocked(dto: BulkSetPipelinesLockedDto) { return this.process.bulkSetPipelinesLocked(dto); }
  bulkDeletePipelines(dto: BulkDeletePipelinesDto) { return this.process.bulkDeletePipelines(dto); }
  previewSpecImport(dto: PreviewSpecImportDto) { return this.process.previewSpecImport(dto); }
  applySpecImport(dto: ApplySpecImportDto) { return this.process.applySpecImport(dto); }
  executePipeline(id: number, dto: ExecuteEllaPipelineDto) { return this.process.execute(id, dto); }
  debugExecutePipeline(id: number, dto: EllaPipelineDebugRequestDto) { return this.process.debugExecute(id, dto); }
  testGenieQuestion(request: TestDatabricksGenieRequest) { return this.process.testGenieQuestion(request); }
  getExecutionHistory(pipelineId: number, page?: number, pageSize?: number, status?: string) {
    return this.process.getExecutionHistory(pipelineId, page, pageSize, undefined, undefined, status);
  }
  getExecutionPayload(pipelineId: number, executionId: number) { return this.process.getExecutionPayload(pipelineId, executionId); }
  lockPipeline(id: number) { return this.process.lock(id); }
  unlockPipeline(id: number) { return this.process.unlock(id); }
  publishPipeline(id: number, dto: PublishPipelineRequest) { return this.process.publish(id, dto); }
  getPipelineReleases(id: number) { return this.process.getReleases(id); }
  getPipelineReleaseDetail(id: number, releaseId: number) { return this.process.getReleaseDetail(id, releaseId); }
  rollbackPipeline(id: number, releaseId: number) { return this.process.rollback(id, releaseId); }

  // --- Promotion (cross-environment export/import) ---
  exportPromotionPackage(dto: ExportPromotionPackageRequest) { return this.promotion.export(dto); }
  getPromotionInbox() { return this.promotion.inbox(); }
  validatePromotion(recordId: number) { return this.promotion.validate(recordId); }
  applyPromotion(recordId: number, overwriteCallerSettings = false) { return this.promotion.apply(recordId, overwriteCallerSettings); }
  getCurrentEnvironment() { return this.promotion.currentEnvironment(); }

  // --- MDH ---
  getUniverses() { return this.mdh.getUniverses(); }
  getUniverse(id: number) { return this.mdh.getUniverse(id); }
  createUniverse(dto: CreateEllaMdhUniverseDto) { return this.mdh.createUniverse(dto); }
  updateUniverse(id: number, dto: UpdateEllaMdhUniverseDto) { return this.mdh.updateUniverse(id, dto); }
  deleteUniverse(id: number) { return this.mdh.deleteUniverse(id); }
  getGoldenRecords(universeId: number, limit?: number, search?: string) { return this.mdh.getGoldenRecords(universeId, limit, search); }
  getGoldenRecord(id: number) { return this.mdh.getGoldenRecord(id); }
  ingestSourceRecord(universeId: number, dto: IngestEllaMdhSourceRecordDto) { return this.mdh.ingestSourceRecord(universeId, dto); }
  getQuarantine(universeId: number, limit?: number) { return this.mdh.getQuarantine(universeId, limit); }
  resolveQuarantine(sourceRecordId: number, dto: ResolveQuarantineDto) { return this.mdh.resolveQuarantine(sourceRecordId, dto); }

  // --- Health ---
  getHealthChecks() { return this.health.getAll(); }
  getHealthCheck(id: number) { return this.health.get(id); }
  createHealthCheck(dto: CreateEllaHealthCheckDto) { return this.health.create(dto); }
  updateHealthCheck(id: number, dto: UpdateEllaHealthCheckDto) { return this.health.update(id, dto); }
  deleteHealthCheck(id: number) { return this.health.delete(id); }
  triggerCheck(id: number) { return this.health.triggerCheck(id); }
  getHealthHistory(id: number, limit?: number) { return this.health.getHistory(id, limit); }

  // --- Issues (pipeline/connection warning system) ---
  getEllaIssuesActive() { return this.issue.getActive(); }
  getEllaIssuesAll(includeDismissed?: boolean, limit?: number) { return this.issue.getAll(includeDismissed, limit); }
  dismissEllaIssue(id: number) { return this.issue.dismiss(id); }
  dismissAllEllaIssues() { return this.issue.dismissAll(); }
  getEllaIssueDetail(id: number) { return this.issue.getDetail(id); }

  // --- Alerts ---
  getRules() { return this.alert.getRules(); }
  getRule(id: number) { return this.alert.getRule(id); }
  createRule(dto: CreateEllaAlertRuleDto) { return this.alert.createRule(dto); }
  updateRule(id: number, dto: UpdateEllaAlertRuleDto) { return this.alert.updateRule(id, dto); }
  deleteRule(id: number) { return this.alert.deleteRule(id); }
  testAlert(id: number) { return this.alert.testAlert(id); }
  getAlertHistory(alertRuleId?: number, limit?: number) { return this.alert.getHistory(alertRuleId, limit); }

  // --- Settings ---
  getSettings() { return this.settings.getAll(); }
  updateSetting(key: string, dto: UpdateEllaSystemSettingDto) { return this.settings.update(key, dto); }

  // --- Environment Variables (promotion-time bindings for literal step fields) ---
  getEnvironmentVariables() { return this.environmentVariable.getAll(); }
  createEnvironmentVariable(dto: CreateEllaEnvironmentVariableRequest) { return this.environmentVariable.create(dto); }
  updateEnvironmentVariable(id: number, dto: UpdateEllaEnvironmentVariableRequest) { return this.environmentVariable.update(id, dto); }
  deleteEnvironmentVariable(id: number) { return this.environmentVariable.delete(id); }
  getEnvironmentVariableUsage(id: number) { return this.environmentVariable.getUsage(id); }

  // --- Key Vaults ---
  getKeyVaults() { return this.keyVault.getKeyVaults(); }
  createKeyVault(dto: CreateEllaKeyVaultDto) { return this.keyVault.createKeyVault(dto); }
  updateKeyVault(id: number, dto: UpdateEllaKeyVaultDto) { return this.keyVault.updateKeyVault(id, dto); }
  deleteKeyVault(id: number) { return this.keyVault.deleteKeyVault(id); }
  testVaultConnectivity(id: number) { return this.keyVault.testVaultConnectivity(id); }
  testVaultPermissions(id: number) { return this.keyVault.testVaultPermissions(id); }
  getSecrets(vaultId: number) { return this.keyVault.getSecrets(vaultId); }
  createSecret(dto: CreateEllaKeyVaultSecretDto) { return this.keyVault.createSecret(dto); }
  updateSecret(secretId: number, dto: UpdateEllaKeyVaultSecretDto) { return this.keyVault.updateSecret(secretId, dto); }
  deleteSecret(secretId: number) { return this.keyVault.deleteSecret(secretId); }
  testSecret(secretId: number) { return this.keyVault.testSecret(secretId); }
  lockKeyVault(id: number) { return this.keyVault.lock(id); }
  unlockKeyVault(id: number) { return this.keyVault.unlock(id); }

  // --- Adapters (open catalog — EllaAdapterClient) ---
  getAdapters() { return this.adapter.getAdapters(); }
  getAdapter(id: number) { return this.adapter.getAdapter(id); }
  getAdapterReleases(id: number) { return this.adapter.getAdapterReleases(id); }
  getAdapterUsage(id: number) { return this.adapter.getUsage(id); }
  getAdapterOperationUsage(id: number, operationIds: string[]) {
    return this.adapter.getOperationUsage(id, new GetAdapterOperationUsageRequest({ operationIds }));
  }

  // --- Adapter Builder (gated authoring — EllaAdapterBuilderClient) ---
  getAdapterDraft(id: number) { return this.adapterBuilder.getDraft(id); }
  createAdapter(dto: CreateEllaAdapterRequest) { return this.adapterBuilder.createAdapter(dto); }
  deleteAdapter(id: number) { return this.adapterBuilder.deleteAdapter(id); }
  updateAdapterDraft(id: number, dto: UpdateEllaAdapterDraftRequest) { return this.adapterBuilder.updateDraft(id, dto); }
  importAdapterOperationsFromSpec(dto: ImportAdapterOperationsFromSpecRequest) { return this.adapterBuilder.importFromSpec(dto); }
  testAdapterOperation(dto: TestAdapterOperationRequest) { return this.adapterBuilder.testOperation(dto); }
  testAdapterOperationWithFile(connectionId: number, operationJson: string, inputsJson: string, file: FileParameter) {
    return this.adapterBuilder.testOperationWithFile(connectionId, operationJson, inputsJson, file);
  }
  publishAdapter(id: number, dto: PublishEllaAdapterRequest) { return this.adapterBuilder.publish(id, dto); }
  rollbackAdapter(id: number, releaseId: number) { return this.adapterBuilder.rollback(id, releaseId); }
  deactivateAdapter(id: number) { return this.adapterBuilder.deactivate(id); }
  reactivateAdapter(id: number) { return this.adapterBuilder.reactivate(id); }
  renameAdapterKey(id: number, dto: RenameEllaAdapterRequest) { return this.adapterBuilder.renameKey(id, dto); }

  // --- Proxy SPs (full CRUD lives in Ella Settings > Proxy SP's; also the picker source for both
  // Machine Callers' and API Keys' Proxy SP Mapping dialogs) ---
  getProxySps() { return this.proxySp.getAll(); }
  createProxySp(dto: CreateEllaProxySpRequest) { return this.proxySp.create(dto); }
  updateProxySp(id: number, dto: UpdateEllaProxySpRequest) { return this.proxySp.update(id, dto); }
  deactivateProxySp(id: number) { return this.proxySp.deactivate(id); }
  deleteProxySp(id: number) { return this.proxySp.delete(id); }

  // --- API Keys (vendor-facing auth) ---
  getApiKeys() { return this.apiKey.getAll(); }
  getApiKey(id: number) { return this.apiKey.get(id); }
  createApiKey(dto: CreateEllaApiKeyRequest) { return this.apiKey.create(dto); }
  rotateApiKey(id: number, dto: RotateEllaApiKeyRequest) { return this.apiKey.rotate(id, dto); }
  revokeApiKey(id: number) { return this.apiKey.revoke(id); }
  getApiKeyEligibleScopes(id: number) { return this.apiKey.getEligibleScopes(id); }

  // --- API Key Proxy SP Mappings ---
  getApiKeyProxySpMappings() { return this.apiKeyProxySpMapping.getAll(); }
  createApiKeyProxySpMapping(dto: CreateEllaApiKeyProxySpMappingRequest) { return this.apiKeyProxySpMapping.create(dto); }
  deactivateApiKeyProxySpMapping(id: number) { return this.apiKeyProxySpMapping.deactivate(id); }
  deleteApiKeyProxySpMapping(id: number) { return this.apiKeyProxySpMapping.delete(id); }

  // --- Caller SP Mappings (machine caller / Managed Identity registry) ---
  getCallerSpMappings() { return this.callerSpMapping.getAll(); }
  createCallerSpMapping(dto: CreateEllaCallerSpMappingRequest) { return this.callerSpMapping.create(dto); }
  updateCallerSpMapping(id: number, dto: UpdateEllaCallerSpMappingRequest) { return this.callerSpMapping.update(id, dto); }
  deactivateCallerSpMapping(id: number) { return this.callerSpMapping.deactivate(id); }
  deleteCallerSpMapping(id: number) { return this.callerSpMapping.delete(id); }

  // --- API Key Scope Grants ---
  grantApiKeyIntegrationScope(dto: GrantIntegrationScopeRequest) { return this.apiKeyScope.grantIntegrationScope(dto); }
  revokeApiKeyIntegrationScope(id: number) { return this.apiKeyScope.revokeIntegrationScope(id); }
  grantApiKeyPipelineScope(dto: GrantPipelineScopeRequest) { return this.apiKeyScope.grantPipelineScope(dto); }
  revokeApiKeyPipelineScope(id: number) { return this.apiKeyScope.revokePipelineScope(id); }
}
